import { useEffect } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { commandCenterApi, type FunnelChannel, type FunnelRange } from '../api/commandCenterApi';
import { agencyKeys, commandCenterKeys } from '../queryKeys';

export function usePracticeReviewFunnel(
  practiceId: string | null,
  params?: { locationId?: string; range?: FunnelRange; channel?: FunnelChannel },
) {
  return useQuery({
    queryKey: commandCenterKeys.practiceReviewFunnel(practiceId!, params),
    queryFn: () => commandCenterApi.getPracticeReviewFunnel(practiceId!, params),
    enabled: !!practiceId,
    staleTime: 60_000,
  });
}

export function usePracticeTextFunnel(
  practiceId: string | null,
  params?: { locationId?: string; range?: FunnelRange },
) {
  return useQuery({
    queryKey: commandCenterKeys.practiceTextFunnel(practiceId!, params),
    queryFn: () => commandCenterApi.getPracticeTextFunnel(practiceId!, params),
    enabled: !!practiceId,
    staleTime: 60_000,
  });
}

export function useAgencyReviewRequests(range: FunnelRange) {
  return useQuery({
    queryKey: commandCenterKeys.reviewRequests(range),
    queryFn: () => commandCenterApi.getReviewRequests(range),
    staleTime: 60_000,
  });
}

export function useSetPracticeSnooze() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ practiceId, snoozed }: { practiceId: string; snoozed: boolean }) =>
      commandCenterApi.setPracticeSnooze(practiceId, snoozed),
    onSuccess: () => {
      // Refresh the command-center tables and any agency practice lists (e.g. the
      // top-bar practice switcher, which hides snoozed practices).
      qc.invalidateQueries({ queryKey: commandCenterKeys.all });
      qc.invalidateQueries({ queryKey: agencyKeys.practices() });
    },
  });
}

export function usePracticeAgents(practiceId: string | null) {
  return useQuery({
    queryKey: commandCenterKeys.practiceAgents(practiceId!),
    queryFn: () => commandCenterApi.getPracticeAgents(practiceId!),
    enabled: !!practiceId,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
}

/** One agent's composed status and tasks. Polls every 10s, every 3s while a run is live. */
export function useAgentStatus(
  practiceId: string,
  locationId: string,
  roleKey: string,
  all = false,
  /** False for a passive reader (a list dot): no polling, a longer stale time. */
  poll = true,
) {
  return useQuery({
    queryKey: commandCenterKeys.agentStatus(practiceId, locationId, roleKey, all),
    queryFn: () => commandCenterApi.getAgentStatus(practiceId, locationId, roleKey, all),
    enabled: !!practiceId && !!locationId && !!roleKey,
    refetchInterval: poll ? (query) => (query.state.data?.run?.activeRun ? 3_000 : 10_000) : false,
    refetchIntervalInBackground: false,
    staleTime: poll ? 5_000 : 60_000,
  });
}

/**
 * The run log, paged newest first. It follows the agent's status (which polls): when a run starts,
 * finishes or the agent wakes, the log and any open task timelines refetch, so a run that just
 * failed shows up without a reload.
 */
export function useAgentRunLog(
  practiceId: string,
  locationId: string,
  roleKey: string,
  enabled = true,
) {
  const qc = useQueryClient();
  const run = useAgentStatus(practiceId, locationId, roleKey).data?.run;
  const activity = run
    ? `${run.latestRun?.id ?? ''}:${run.activeRun?.runId ?? ''}:${run.lastWake?.at ?? ''}`
    : null;
  useEffect(() => {
    if (activity === null) return;
    qc.invalidateQueries({ queryKey: commandCenterKeys.runLog(practiceId, locationId, roleKey) });
    qc.invalidateQueries({ queryKey: commandCenterKeys.taskTimelines(practiceId, locationId) });
  }, [activity, qc, practiceId, locationId, roleKey]);

  return useInfiniteQuery({
    queryKey: commandCenterKeys.runLog(practiceId, locationId, roleKey),
    queryFn: ({ pageParam }) =>
      commandCenterApi.getRunLog(practiceId, locationId, roleKey, pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    enabled: enabled && !!practiceId && !!locationId && !!roleKey,
  });
}

/** One agent's observations, newest first, paged; fetched only while their panel is open. */
export function useAgentObservations(
  practiceId: string,
  locationId: string,
  roleKey: string,
  enabled: boolean,
) {
  return useInfiniteQuery({
    queryKey: commandCenterKeys.observations(practiceId, locationId, roleKey),
    queryFn: ({ pageParam }) =>
      commandCenterApi.getObservations(practiceId, locationId, roleKey, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: enabled && !!practiceId && !!locationId && !!roleKey,
    staleTime: 10_000,
  });
}

/** The client facts at one location; fetched only while their panel is open. */
export function useClientFacts(practiceId: string, locationId: string, enabled: boolean) {
  return useQuery({
    queryKey: commandCenterKeys.clientFacts(practiceId, locationId),
    queryFn: () => commandCenterApi.getClientFacts(practiceId, locationId),
    enabled: enabled && !!practiceId && !!locationId,
    staleTime: 10_000,
  });
}

export function useRetireClientFacts(practiceId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (factIds: string[]) =>
      commandCenterApi.retireClientFacts(practiceId, locationId, factIds),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: commandCenterKeys.clientFacts(practiceId, locationId) }),
  });
}

/** One task's timeline; fetched only while the task is expanded. */
export function useAgentTaskTimeline(
  practiceId: string,
  locationId: string,
  taskId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: commandCenterKeys.taskTimeline(practiceId, locationId, taskId),
    queryFn: () => commandCenterApi.getTaskTimeline(practiceId, locationId, taskId),
    enabled,
    staleTime: 10_000,
  });
}

/**
 * Close one open task from its Command Center row. The statuses, the practice's agents (schedules and
 * their Run now) and the practice's task list all change with it.
 */
export function useCloseAgentTask(practiceId: string, locationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => commandCenterApi.closeTask(practiceId, locationId, taskId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commandCenterKeys.agentStatuses(practiceId) });
      qc.invalidateQueries({ queryKey: commandCenterKeys.taskTimelines(practiceId, locationId) });
      qc.invalidateQueries({ queryKey: commandCenterKeys.practiceAgents(practiceId) });
    },
  });
}

/** Read-only mission review data for one location. Disabled mission mode still returns a snapshot. */
export function usePracticeMission(
  practiceId: string | null,
  locationId: string | null,
  roleKey: string | null,
) {
  return useQuery({
    queryKey: commandCenterKeys.practiceMission(practiceId ?? '', locationId ?? '', roleKey ?? ''),
    queryFn: () =>
      commandCenterApi.getPracticeMission(practiceId!, locationId!, roleKey ?? undefined),
    enabled: !!practiceId && !!locationId && !!roleKey,
    // Unconditional: the mission engine is the only execution path, so there is no state in
    // which this snapshot stops changing on its own.
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
}

/** The location's day-one report, or null when the agents have not written one yet. */
export function useLocationDayOneReport(practiceId: string | null, locationId: string | null) {
  return useQuery({
    queryKey: commandCenterKeys.dayOneReport(practiceId ?? '', locationId ?? ''),
    queryFn: () => commandCenterApi.getLocationDayOneReport(practiceId!, locationId!),
    enabled: !!practiceId && !!locationId,
    staleTime: 60_000,
  });
}

function useAgentMutation<TArgs>(practiceId: string, fn: (args: TArgs) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commandCenterKeys.practiceAgents(practiceId) });
      // Hire, pause and schedule changes all move the composed status.
      qc.invalidateQueries({ queryKey: commandCenterKeys.agentStatuses(practiceId) });
    },
  });
}

export function useSetLocationAgent(practiceId: string) {
  return useAgentMutation(
    practiceId,
    (args: { locationId: string; roleKey: string; status?: 'active' | 'paused' }) =>
      commandCenterApi.setLocationAgent(practiceId, args.locationId, args.roleKey, {
        ...(args.status && { status: args.status }),
      }),
  );
}

export function useUnassignLocationAgent(practiceId: string) {
  return useAgentMutation(practiceId, (args: { locationId: string; roleKey: string }) =>
    commandCenterApi.unassignLocationAgent(practiceId, args.locationId, args.roleKey),
  );
}

export function useRunLocationAgent(practiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { locationId: string; roleKey: string }) =>
      commandCenterApi.runLocationAgent(practiceId, args.locationId, args.roleKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commandCenterKeys.practiceAgents(practiceId) });
      // The run starts now: pick up Working (and its faster poll) without waiting for the next tick.
      qc.invalidateQueries({ queryKey: commandCenterKeys.agentStatuses(practiceId) });
    },
  });
}

export function useUpdateAgentSchedule(practiceId: string) {
  return useAgentMutation(
    practiceId,
    (args: {
      locationId: string;
      roleKey: string;
      scheduleId: string;
      override: { intervalDays?: number | null; quotaCount?: number | null };
    }) =>
      commandCenterApi.updateAgentSchedule(
        practiceId,
        args.locationId,
        args.roleKey,
        args.scheduleId,
        args.override,
      ),
  );
}

export function useRunAgentSchedule(practiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { locationId: string; roleKey: string; scheduleId: string }) =>
      commandCenterApi.runAgentSchedule(practiceId, args.locationId, args.roleKey, args.scheduleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commandCenterKeys.practiceAgents(practiceId) });
      qc.invalidateQueries({ queryKey: commandCenterKeys.agentStatuses(practiceId) });
    },
  });
}

export function usePauseAgentSchedule(practiceId: string) {
  return useAgentMutation(
    practiceId,
    (args: { locationId: string; roleKey: string; scheduleId: string; paused: boolean }) =>
      commandCenterApi.setAgentSchedulePaused(
        practiceId,
        args.locationId,
        args.roleKey,
        args.scheduleId,
        args.paused,
      ),
  );
}

export function useWaiveAgentSchedule(practiceId: string) {
  return useAgentMutation(
    practiceId,
    (args: { locationId: string; roleKey: string; scheduleId: string; reason: string }) =>
      commandCenterApi.waiveAgentSchedule(
        practiceId,
        args.locationId,
        args.roleKey,
        args.scheduleId,
        args.reason,
      ),
  );
}

export function useResetAgent(practiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { locationId: string; roleKey: string; confirmName: string }) =>
      commandCenterApi.resetAgent(practiceId, args.locationId, args.roleKey, args.confirmName),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: commandCenterKeys.practiceAgents(practiceId) }),
        qc.invalidateQueries({ queryKey: commandCenterKeys.practiceMissions(practiceId) }),
        qc.invalidateQueries({ queryKey: commandCenterKeys.agentStatuses(practiceId) }),
      ]),
  });
}

export function useResetLocationAgents(practiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { locationId: string; confirmName: string }) =>
      commandCenterApi.resetLocationAgents(practiceId, args.locationId, args.confirmName),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: commandCenterKeys.practiceAgents(practiceId) }),
        qc.invalidateQueries({ queryKey: commandCenterKeys.practiceMissions(practiceId) }),
        qc.invalidateQueries({ queryKey: commandCenterKeys.agentStatuses(practiceId) }),
      ]),
  });
}
